import { cloneDeep } from 'lodash';
import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, ILayerProps, StateType } from '@/version-types';
import { DEFAULT_INTELLIGENT_PRE_DATA, intelligentColors, SMEAR_PEN_SIZE } from '../constants';
import {
    IIntelligentColorItem,
    IIntelligentEnhanceParams,
    IIntelligentPreviewItem,
    IItIntelligentHSLSelect,
    intelligentColorType,
    ISelectInfo,
    PEN_TYPE,
} from '../types';

/** 状态 */
export interface IState {
    /** 原始图层信息 */
    originLayer: ILayerProps | null;
    extractColors: string[];
    // TODO: 原来依赖 isIntelligent 的替换处理
    /** 是否一键换色操作 */
    isIntelligent: boolean;

    /** 一键换色，色块列表 */
    selectedIndex: number;
    selectedInfo: IIntelligentColorItem;
    intelligentColors: IIntelligentColorItem[];
    intelligentPreviewList: IIntelligentPreviewItem[];
    intelligentHSLSelectInfo: IIntelligentEnhanceParams | null;
    intelligentPreData: IIntelligentEnhanceParams;
    selectedHSLIndex: number;
    isOpenIntelligentPickColor: boolean;
    // TODO: 原来依赖 isProtectPen 的替换处理
    /** 是否正在执行一键换色画笔操作 */
    isProtectPen: boolean;
    smartPenType: PEN_TYPE;
    /** 一键换色，画笔大小 */
    intelligentPenSize: number;
    /** 一键换色，保护区域 */
    protectMask: HTMLCanvasElement | null;
}

/** 动作 */
export interface IAction {
    setIsIntelligent: (isOpen: boolean) => void;
    setOriginLayer: (layer: ILayerProps | null) => void;
    setExtractColors: (colors: string[]) => void;
    setSelectedData: (info: ISelectInfo) => void;
    initIntelligentColors: (colors: string[]) => void;
    intelligentDestroy: () => void;
    setIntelligentHSLSelectInfo: (params: IItIntelligentHSLSelect) => void;
    resetIntelligentHSLSelectInfo: () => void;
    setSelectedHSLIndex: (index: number) => void;
    setIntelligentPreviewList: (params: IIntelligentEnhanceParams[]) => void;
    replaceIntelligentPickColor: (color: string) => void;
    changeSelectedColor: (params: ISelectInfo) => void;
    setIntelligentPreData: (params: IIntelligentEnhanceParams) => void;
    setIsOpenIntelligentPickColor: (isOpen: boolean) => void;
    setSmartPenType: (type: PEN_TYPE) => void;
    setIntelligentPenSize: (penSize: number) => void;
    setIsProtectPen: (isOpen: boolean) => void;
    setProtectMask: (mask: HTMLCanvasElement | null) => void;
    destroy: () => void;
}

/** 存储类型 */
export interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState(): IState {
    return {
        isIntelligent: false,
        originLayer: null,
        extractColors: [],
        selectedIndex: -1,
        selectedInfo: cloneDeep(intelligentColors()[0]),
        intelligentColors: [],
        selectedHSLIndex: -1,
        intelligentHSLSelectInfo: null,
        intelligentPreviewList: [],
        intelligentPreData: cloneDeep(DEFAULT_INTELLIGENT_PRE_DATA),
        isOpenIntelligentPickColor: false,
        isProtectPen: false,
        smartPenType: PEN_TYPE.BRUSH,
        protectMask: null,
        intelligentPenSize: SMEAR_PEN_SIZE,
    };
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),
        setIsIntelligent(isOpen) {
            set({ isIntelligent: isOpen });
        },
        setOriginLayer(layer) {
            set({ originLayer: layer });
        },
        setExtractColors(colors) {
            set({ extractColors: colors });
        },

        setSelectedData(info) {
            set({
                selectedIndex: info.index,
                selectedInfo: cloneDeep(info),
            });
        },

        initIntelligentColors(colors) {
            const configs = cloneDeep(intelligentColors());
            colors.forEach((color) => {
                configs.push({ color, type: intelligentColorType.COLOR });
            });

            set({
                intelligentColors: configs,
                selectedIndex: -1,
                selectedInfo: cloneDeep(intelligentColors()[0]),
            });
        },

        intelligentDestroy() {
            set({
                intelligentPreviewList: [],
                selectedHSLIndex: -1,
                selectedIndex: -1,
                selectedInfo: cloneDeep(intelligentColors()[0]),
                intelligentHSLSelectInfo: null,
                intelligentPreData: cloneDeep(DEFAULT_INTELLIGENT_PRE_DATA),
                isOpenIntelligentPickColor: false,
                intelligentPenSize: SMEAR_PEN_SIZE,
            });
        },

        setIntelligentHSLSelectInfo({ info, index = -1 }) {
            set({
                selectedHSLIndex: index,
                intelligentHSLSelectInfo: cloneDeep(info),
            });
        },

        resetIntelligentHSLSelectInfo() {
            set({
                selectedHSLIndex: -1,
                intelligentHSLSelectInfo: null,
            });
        },

        setSelectedHSLIndex(index) {
            set({ selectedHSLIndex: index });
        },

        setIntelligentPreviewList(list) {
            set({ intelligentPreviewList: cloneDeep(list) });
        },

        replaceIntelligentPickColor(color) {
            const pickColor = get().intelligentColors[0];
            pickColor.color = color;

            const newIntelligentColors = [...get().intelligentColors];
            newIntelligentColors.splice(0, 1, pickColor);

            set({
                intelligentColors: newIntelligentColors,
                selectedIndex: 0,
                selectedInfo: cloneDeep(pickColor),
            });
        },

        changeSelectedColor() {},

        setIntelligentPreData(data) {
            set({
                intelligentPreData: cloneDeep(data),
            });
        },

        setIsOpenIntelligentPickColor(isOpen) {
            set({ isOpenIntelligentPickColor: isOpen });
        },

        setIsProtectPen(isOpen) {
            set({ isProtectPen: isOpen });
        },

        setSmartPenType(type) {
            set({ smartPenType: type });
        },

        setIntelligentPenSize(penSize) {
            set({ intelligentPenSize: penSize });
        },

        setProtectMask(mask) {
            set({ protectMask: mask });
        },

        destroy() {
            set(getInitialState());
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'intelligent-color-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useIntelligentColorState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getIntelligentColorState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

export const watchIntelligentColorChange = (identity: string, callback: (state: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState) !== JSON.stringify(state)) {
            callback(state);
        }
    });
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
